// src/components/MembershipEndAdmin.js
// Payment Settings "End a membership" (admin only; FLIP_GAPS_DESIGN.md
// section 2; flip-day checklist B6, and C6 step 7's dark check).
//
// One form, one callable: adminEndMembership (through the existing wrapper
// in utils/membershipPayment.js), sent only after the admin confirms. The
// server checks the caller again, refunds, ends, sends the member the ending
// notice and writes the membership_admin_end audit row; nothing here reads
// membershipBilling (server only). The result line comes from the reply.
//
// Each uid shows its public name (public_profiles/{uid}.userName, public
// read) so the admin sees who is being ended before confirming. "Use my
// account" fills the signed-in admin's uid as the member (the dark check).
//
// Kind (evergreen 3.5b): Channel (the creator uid, as before) or Team (the
// team id in place of the creator uid). A team id is never looked up: the
// one Firestore read stays public_profiles, for the uids only.
//
// Strings, form checks and payloads: utils/membershipEndAdmin.js.

import React, { useEffect, useRef, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Switch, TextInput, Alert, Platform,
} from 'react-native';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';
import { adminEndMembership } from '../utils/membershipPayment';
import {
  END_COPY as C,
  KINDS,
  KIND_LABELS,
  GROUNDS,
  GROUND_LABELS,
  WHENS,
  WHEN_LABELS,
  NOTE_MAX,
  DEFAULT_FORM,
  isMembershipUid,
  effectiveRefund,
  nameLine,
  planEnd,
  resultLine,
  endErrorText,
} from '../utils/membershipEndAdmin';

const COLORS = {
  CARD: '#111',
  GOLD: '#FCD000',
  TEXT: '#fff',
  DIM: '#888',
  BORDER: '#222',
  DANGER: '#ff4444',
  OK: '#4CAF50',
};

const NAME_LOOKUP_DELAY_MS = 400;

function confirmAsk(message) {
  if (Platform.OS === 'web') return Promise.resolve(typeof window !== 'undefined' ? window.confirm(message) : false);
  return new Promise((resolve) => {
    Alert.alert(C.title, message, [
      { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
      { text: 'OK', style: 'destructive', onPress: () => resolve(true) },
    ]);
  });
}

const NO_NAME = Object.freeze({ uid: null, name: null, missing: false });

// The public name for a valid uid, looked up a moment after typing stops.
// A read error shows nothing (not "no public profile").
function useProfileName(uid) {
  const [state, setState] = useState(NO_NAME);
  useEffect(() => {
    if (!isMembershipUid(uid)) return undefined;
    let active = true;
    const t = setTimeout(() => {
      getDoc(doc(db, 'public_profiles', uid))
        .then((snap) => {
          if (!active) return;
          const n = snap.exists() ? snap.data()?.userName : null;
          const name = typeof n === 'string' && n.trim() ? n.trim() : null;
          setState({ uid, name, missing: !name });
        })
        .catch(() => { if (active) setState({ uid, name: null, missing: false }); });
    }, NAME_LOOKUP_DELAY_MS);
    return () => { active = false; clearTimeout(t); };
  }, [uid]);
  return state.uid === uid ? state : NO_NAME;
}

export default function MembershipEndAdmin() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);   // a form check or the server's sentence
  const [result, setResult] = useState(null);     // resultLine(reply)
  const inFlight = useRef(false);                 // covers the confirm too (native Alert is async)

  const isTeam = form.kind === 'team';
  // A team id is never a uid: no name lookup for it.
  const creatorName = useProfileName(isTeam ? '' : form.creatorUid.trim());
  const viewerName = useProfileName(form.viewerUid.trim());
  const eff = effectiveRefund(form);

  // Any edit clears the last problem and result, so a line never sits next
  // to values it was not about.
  const set = (patch) => {
    setForm((cur) => ({ ...cur, ...patch }));
    setProblem(null);
    setResult(null);
  };

  const fillMine = () => {
    const mine = auth.currentUser?.uid;
    if (mine) set({ viewerUid: mine });
  };

  const onEnd = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setProblem(null);
    setResult(null);
    try {
      const plan = planEnd(form, { creator: creatorName.name, viewer: viewerName.name });
      if (plan.error) { setProblem(plan.error); return; }
      if (!(await confirmAsk(plan.confirmText))) return;
      setBusy(true);
      try {
        const reply = await adminEndMembership(plan.payload);
        setResult(resultLine(reply, plan.payload.when));
      } catch (e) {
        console.warn('[membership-end] adminEndMembership failed:', e?.code || '', e?.message || '');
        setProblem(endErrorText(e, plan.payload.kind === 'team' ? 'team' : 'channel'));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- pieces
  const nameRow = (role, n) => {
    if (n.name) return <Text style={styles.nameText}>{nameLine(role, n.name)}</Text>;
    if (n.missing) return <Text style={styles.hint}>{C.noProfile}</Text>;
    return null;
  };

  const options = (key, values, labels) => (
    <View style={styles.optionRow}>
      {values.map((v) => {
        const on = form[key] === v;
        return (
          <TouchableOpacity
            key={v}
            style={[styles.option, on && styles.optionOn]}
            onPress={() => set({ [key]: v })}
            disabled={busy}
            accessibilityRole="radio"
            accessibilityState={{ checked: on, disabled: busy }}
          >
            <Text style={[styles.optionText, on && styles.optionTextOn]}>{labels[v]}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const toggle = (label, value, onChange, off = false) => (
    <View style={styles.toggleRow}>
      <Text style={[styles.toggleLabel, off && styles.dimText]}>{label}</Text>
      <Switch
        value={value}
        onValueChange={onChange}
        disabled={busy || off}
        accessibilityLabel={label}
        trackColor={{ false: '#333', true: '#4CAF50' }}
        thumbColor={value ? '#fff' : '#888'}
      />
    </View>
  );

  const uidInput = (label, key, extraStyle) => (
    <TextInput
      style={[styles.input, extraStyle]}
      value={form[key]}
      onChangeText={(v) => set({ [key]: v })}
      accessibilityLabel={label}
      autoCapitalize="none"
      autoCorrect={false}
      placeholderTextColor="#666"
      editable={!busy}
    />
  );

  // ---------------------------------------------------------------- render
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{C.title}</Text>
      <Text style={styles.intro}>{C.intro}</Text>

      <Text style={styles.fieldLabel}>{C.kind}</Text>
      {options('kind', KINDS, KIND_LABELS)}

      {isTeam ? (
        <>
          <Text style={styles.fieldLabel}>{C.teamId}</Text>
          {uidInput(C.teamId, 'teamId')}
        </>
      ) : (
        <>
          <Text style={styles.fieldLabel}>{C.creatorUid}</Text>
          {uidInput(C.creatorUid, 'creatorUid')}
          {nameRow('creator', creatorName)}
        </>
      )}

      <Text style={styles.fieldLabel}>{C.viewerUid}</Text>
      <View style={styles.inputRow}>
        {uidInput(C.viewerUid, 'viewerUid', styles.inputFlex)}
        <TouchableOpacity
          style={[styles.smallBtn, busy && styles.btnDisabled]}
          onPress={fillMine}
          disabled={busy}
          accessibilityRole="button"
        >
          <Text style={styles.smallBtnText}>{C.useMine}</Text>
        </TouchableOpacity>
      </View>
      {nameRow('viewer', viewerName)}

      <Text style={styles.fieldLabel}>{C.subscriptionId}</Text>
      {uidInput(C.subscriptionId, 'subscriptionId')}
      <Text style={styles.hint}>{C.subscriptionHint}</Text>

      <Text style={styles.fieldLabel}>{C.reason}</Text>
      {options('ground', GROUNDS, GROUND_LABELS)}

      <Text style={styles.fieldLabel}>{C.when}</Text>
      {options('when', WHENS, WHEN_LABELS)}

      {toggle(C.refund, eff.refundLatest, (v) => set({ refund: v }), form.when !== 'now')}
      {form.when !== 'now' ? <Text style={styles.hint}>{C.refundOffHint}</Text> : null}
      {eff.waiveShown ? (
        <>
          {toggle(C.waive, form.waive === true, (v) => set({ waive: v }))}
          <Text style={styles.hint}>{C.waiveHint}</Text>
        </>
      ) : null}

      <Text style={styles.fieldLabel}>{C.note}</Text>
      <TextInput
        style={[styles.input, styles.noteInput]}
        value={form.note}
        onChangeText={(v) => set({ note: v })}
        accessibilityLabel={C.note}
        maxLength={NOTE_MAX}
        placeholderTextColor="#666"
        editable={!busy}
        multiline
      />

      <TouchableOpacity
        style={[styles.endBtn, busy && styles.btnDisabled]}
        onPress={onEnd}
        disabled={busy}
        accessibilityRole="button"
      >
        <Text style={styles.endBtnText}>{busy ? C.submitting : C.submit}</Text>
      </TouchableOpacity>
      {problem ? <Text style={styles.errorText}>{problem}</Text> : null}
      {result ? <Text style={styles.resultText}>{result}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: COLORS.CARD, borderRadius: 12, padding: 16, marginBottom: 16,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  sectionTitle: {
    color: COLORS.GOLD, fontSize: 14, fontWeight: '700', marginBottom: 12,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  intro: { color: '#aaa', fontSize: 12.5, lineHeight: 18 },
  hint: { color: COLORS.DIM, fontSize: 12, marginTop: 6, lineHeight: 17 },
  dimText: { color: COLORS.DIM },
  nameText: { color: COLORS.TEXT, fontSize: 12.5, marginTop: 6, fontWeight: '700' },
  fieldLabel: { color: COLORS.DIM, fontSize: 12, marginBottom: 6, marginTop: 14 },
  input: {
    backgroundColor: '#1a1a1a', borderRadius: 8, padding: 12, color: COLORS.TEXT,
    fontSize: 15, borderWidth: 1, borderColor: '#333',
  },
  inputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  inputFlex: { flex: 1, minWidth: 0 },
  noteInput: { minHeight: 56, fontSize: 14 },
  smallBtn: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, backgroundColor: COLORS.GOLD, alignItems: 'center' },
  smallBtnText: { color: '#000', fontSize: 12.5, fontWeight: '700' },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  option: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: '#2a2a2a' },
  optionOn: { borderColor: COLORS.GOLD, backgroundColor: 'rgba(252, 208, 0, 0.06)' },
  optionText: { color: '#ccc', fontSize: 13 },
  optionTextOn: { color: COLORS.TEXT, fontWeight: '700' },
  toggleRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 10, marginTop: 8, borderBottomWidth: 1, borderBottomColor: COLORS.BORDER,
  },
  toggleLabel: { color: COLORS.TEXT, fontSize: 14, flex: 1, marginRight: 12 },
  endBtn: {
    borderRadius: 8, paddingVertical: 12, alignItems: 'center', marginTop: 16,
    borderWidth: 1, borderColor: COLORS.DANGER,
  },
  endBtnText: { color: COLORS.DANGER, fontSize: 14, fontWeight: '800' },
  btnDisabled: { opacity: 0.5 },
  errorText: { color: COLORS.DANGER, fontSize: 13, marginTop: 10, lineHeight: 18 },
  resultText: { color: COLORS.OK, fontSize: 13, marginTop: 10, lineHeight: 18, fontWeight: '700' },
});
